import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, Pencil, Plus, ShieldCheck, X } from 'lucide-react';
import { AiSurface } from '@/components/ui/ai-surface';
import { Button } from '@/components/ui/button';
import { CitationChip, type CitationChipData } from '@/components/ui/citation-chip';
import { ErrorMessage } from '@/components/ui/error-message';
import { IvvGateChip } from '@/components/ui/ivv-gate-chip';
import { PostureChip } from '@/components/ui/posture-chip';
import { ProvenanceChip } from '@/components/ui/provenance-chip';
import { Spinner } from '@/components/ui/spinner';
import { VerbChip } from '@/components/ui/verb-chip';
import { CitationSourceModal } from '@/components/modals/citation-source-modal';
import {
  DELIVERABLE_LEGAL_TRANSITIONS,
  isIvvGate,
  useCanFlagIvvGate,
  useCreateDeliverableMutation,
  useDeliverableHistoryQuery,
  useTransitionDeliverableMutation,
  useUpdateDeliverableMutation,
  type DeliverableDetails,
  type EdlEntry,
} from '@/lib/api/deliverables';
import { useOverrideTaxonomyQuery } from '@/lib/api/overrides';
import { labelledError } from '@/lib/api/errors';
import { cn } from '@/lib/utils/cn';
import { formatDay, relativeDays } from './checkpoint-status';
import {
  TERMINAL_TARGETS,
  sourceWord,
  deliverableState,
  statusWord,
  transitionLabel,
  verificationLabel,
} from './deliverable-status';

/**
 * Right-hand drawer for one deliverable (E06-F03).
 *
 * view   — what the vendor hands over, when, payment tie, acceptance
 *          criteria, SOW source, the submittal status with its legal next
 *          steps, dual-axis verification, and the submittal history.
 * edit   — the contract facts. AI-extracted deliverables change only through
 *          a structured override (reason tag + rationale, E04-F02); the
 *          caller opens the re-evaluation tray with the returned override.
 * create — the same form for a deliverable added by hand.
 *
 * The Baseline tab opens the same drawer for an EDL milestone (E19 /
 * T-0218): the kicker says Milestone, the header carries the "IV&V gate"
 * chip when the vendor contract names it as one, and an engagement lead
 * toggles that flag in the edit form.
 */

export type DeliverableDrawerMode =
  | { mode: 'view'; entryId: string }
  | { mode: 'edit'; entryId: string }
  | { mode: 'create' };

interface DeliverableDrawerProps {
  engagementId: string;
  entries: ReadonlyArray<EdlEntry>;
  state: DeliverableDrawerMode | null;
  onStateChange: (next: DeliverableDrawerMode | null) => void;
  onVerify: (entry: EdlEntry) => void;
  onOverrideCaptured: (overrideId: string, reasonTag: string) => void;
}

export function DeliverableDrawer({
  engagementId,
  entries,
  state,
  onStateChange,
  onVerify,
  onOverrideCaptured,
}: DeliverableDrawerProps) {
  const entry =
    state && state.mode !== 'create'
      ? (entries.find((e) => e.register_entry_id === state.entryId) ?? null)
      : null;
  const open = state !== null && (state.mode === 'create' || entry !== null);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onStateChange(null)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[600px] flex-col bg-paper-0 shadow-modal focus:outline-none"
        >
          {state?.mode === 'view' && entry ? (
            <ViewPane
              engagementId={engagementId}
              entry={entry}
              onEdit={() => onStateChange({ mode: 'edit', entryId: entry.register_entry_id })}
              onVerify={() => onVerify(entry)}
            />
          ) : state?.mode === 'edit' && entry ? (
            <EditPane
              key={entry.register_entry_id}
              engagementId={engagementId}
              entry={entry}
              onDone={() => onStateChange({ mode: 'view', entryId: entry.register_entry_id })}
              onOverrideCaptured={(id, tag) => {
                onStateChange(null);
                onOverrideCaptured(id, tag);
              }}
            />
          ) : state?.mode === 'create' ? (
            <EditPane
              key="create"
              engagementId={engagementId}
              entry={null}
              onDone={() => onStateChange(null)}
              onOverrideCaptured={() => onStateChange(null)}
            />
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ===========================================================================
// Shared bits
// ===========================================================================

function DrawerHeader({
  kicker,
  title,
  children,
}: {
  kicker: string;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-ink-100 px-6 pb-4 pt-5">
      <div className="min-w-0">
        <div className="text-meta font-semibold uppercase tracking-wide text-ink-500">{kicker}</div>
        <Dialog.Title className="mt-1 text-h2 font-semibold text-ink-900">{title}</Dialog.Title>
        {children}
      </div>
      <Dialog.Close
        aria-label="Close"
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-card border border-ink-200 text-ink-700 hover:bg-paper-100"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </Dialog.Close>
    </div>
  );
}

const SUBMISSION_VERDICT_LABELS: Record<string, string> = {
  met: 'Met',
  partially_met: 'Partially met',
  not_met: 'Not met',
  not_evidenced: 'Not shown in the document',
};

function Section({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-meta font-semibold text-ink-700">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function deliverableCitationChip(e: EdlEntry): CitationChipData | null {
  const s = e.source_citation;
  if (!s) return null;
  return {
    id: s.citation_id,
    doc_label: s.document_title.replace(/\.(pdf|docx?)$/i, ''),
    section: s.section_path,
    page: s.page_number,
    confidence: s.confidence,
  };
}

function paymentWord(v: boolean | null): string {
  return v === true ? 'Payment tied' : v === false ? 'Not payment tied' : 'Not stated';
}

function itemWord(e: EdlEntry | null): string {
  return e?.item_type === 'milestone' ? 'Milestone' : 'Deliverable';
}

/** Mirrors the API's MIN_TRANSITION_REASON_CHARS: one short sentence. */
const MIN_REASON_CHARS = 10;

/** The outcomes the API requires a reason for: the vendor reads it either way. */
const REASON_REQUIRED_FOR: ReadonlySet<string> = new Set(['revise_resubmit', 'rejected']);

/** The transition form's field labels, for a validation error's words (C2-31). */
const TRANSITION_FIELDS: Record<string, string> = {
  reason: 'Reason',
  to_status: 'Status',
  ai_run_id: 'AI run',
  override: 'Override',
};

// ===========================================================================
// View
// ===========================================================================

function ViewPane({
  engagementId,
  entry: e,
  onEdit,
  onVerify,
}: {
  engagementId: string;
  entry: EdlEntry;
  onEdit: () => void;
  onVerify: () => void;
}) {
  const state = deliverableState(e);
  const verification = verificationLabel(e);
  const history = useDeliverableHistoryQuery(e.register_entry_id);
  const transition = useTransitionDeliverableMutation(engagementId);
  const [confirmTo, setConfirmTo] = useState<string | null>(null);
  // The reason a send-back (C2-21) or a rejection (C2-63) carries: stored on
  // the transition, shown in history and on the drawer while the vendor revises.
  const [reason, setReason] = useState('');
  const [openCite, setOpenCite] = useState<CitationChipData | null>(null);
  const cite = deliverableCitationChip(e);
  // Milestones share the submittal steps (C2-18): reported reached, then accepted or not.
  const nextSteps = DELIVERABLE_LEGAL_TRANSITIONS[e.status] ?? [];
  const primary = nextSteps.find((s) => s !== 'overdue' && s !== 'rejected') ?? null;
  const secondary = nextSteps.filter((s) => s !== primary && s !== 'overdue');
  const reasonOk = reason.trim().length >= MIN_REASON_CHARS;

  /** Final outcomes and a send-back are confirmed in place; the rest move at once. */
  function needsStep(to: string): boolean {
    return TERMINAL_TARGETS.has(to) || to === 'revise_resubmit';
  }

  async function move(to: string) {
    const needsReason = REASON_REQUIRED_FOR.has(to);
    if (needsReason && !reasonOk) return;
    try {
      await transition.mutateAsync({
        registerEntryId: e.register_entry_id,
        toStatus: to,
        ...(needsReason ? { reason: reason.trim() } : {}),
        idempotencyKey: crypto.randomUUID(),
      });
      setConfirmTo(null);
      setReason('');
    } catch {
      // Surfaced through the mutation's error state.
    }
  }

  return (
    <>
      <DrawerHeader kicker={`${e.sow_ref ?? 'No SOW ref'} · ${itemWord(e)}`} title={e.title}>
        <div className="mt-2 flex items-center gap-2">
          <VerbChip verb={state.verb} />
          {isIvvGate(e) ? <IvvGateChip /> : null}
          {state.detail ? <span className="text-meta text-ink-500">{state.detail}</span> : null}
        </div>
      </DrawerHeader>

      <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
        <dl className="grid grid-cols-3 divide-x divide-ink-100 rounded-card border border-ink-100">
          <div className="px-4 py-3">
            <dt className="text-meta text-ink-500">Due</dt>
            <dd className="mt-0.5 font-mono text-body-sm font-medium text-ink-900">
              {e.expected_delivery_date ? formatDay(e.expected_delivery_date) : 'No date'}
            </dd>
            <dd className="text-meta text-ink-500">
              {e.expected_delivery_date
                ? relativeDays(e.expected_delivery_date)
                : e.due_text
                  ? `SOW: ${e.due_text}`
                  : 'set one in Edit'}
            </dd>
          </div>
          <div className="px-4 py-3">
            <dt className="text-meta text-ink-500">Payment</dt>
            <dd className="mt-0.5 text-body-sm font-medium text-ink-900">
              {paymentWord(e.is_payment_tied)}
            </dd>
            {e.related_milestone ? (
              <dd className="text-meta text-ink-500">Gates: {e.related_milestone}</dd>
            ) : null}
          </div>
          <div className="px-4 py-3">
            <dt className="text-meta text-ink-500">Origin</dt>
            <dd className="mt-1">
              <ProvenanceChip
                origin={
                  e.origin === 'manually_authored'
                    ? 'manual'
                    : e.origin === 'ai_extracted_edited_by_consultant'
                      ? 'consultant_edited'
                      : 'ai_confirmed'
                }
              />
            </dd>
            {e.category ? <dd className="mt-1 text-meta text-ink-500">{e.category}</dd> : null}
          </div>
        </dl>

        <Section title="What the vendor hands over">
          <p className="text-body-sm text-ink-700">
            {e.description ?? <span className="text-ink-500">Not described. Add it in Edit.</span>}
          </p>
        </Section>

        <Section title={`Acceptance criteria · ${e.acceptance_criteria.length}`}>
          {e.acceptance_criteria.length > 0 ? (
            <ol className="divide-y divide-ink-100 rounded-card border border-ink-100">
              {e.acceptance_criteria.map((c, i) => (
                <li
                  key={`${i}-${c}`}
                  className="grid grid-cols-[22px_1fr] items-start gap-3 px-3 py-2.5 text-body-sm text-ink-900"
                >
                  <span className="font-mono text-mono-sm text-ink-300">{i + 1}</span>
                  <span>{c}</span>
                </li>
              ))}
            </ol>
          ) : (
            <div className="rounded-card border border-dashed border-ink-200 bg-paper-50 px-4 py-3">
              <p className="text-body-sm font-medium text-ink-900">None recorded</p>
              <p className="mt-0.5 text-body-sm text-ink-700">
                Without acceptance criteria there is nothing objective to approve or reject the
                submission against. Add what the SOW or the PMO requires.
              </p>
            </div>
          )}
        </Section>

        {e.submission_review ? (
          <Section title="AI reading of the submission">
            <AiSurface className="rounded-card p-3">
              <p className="text-body-sm text-ink-700">
                Read from{' '}
                <span className="font-medium text-ink-900">
                  {e.submission_review.document_title ?? 'the submitted document'}
                </span>
                {e.submission_review.reviewed_at
                  ? ` on ${formatDay(e.submission_review.reviewed_at.slice(0, 10))}`
                  : ''}
                . This is a reading to help your review, not a decision.
              </p>
              {e.submission_review.criteria.length > 0 ? (
                <ul className="mt-2 space-y-2">
                  {e.submission_review.criteria.map((c, i) => (
                    <li key={`${i}-${c.criterion}`} className="text-body-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={cn(
                            'rounded-full border px-2 py-0.5 text-meta font-medium',
                            c.verdict === 'met'
                              ? 'border-posture-healthy text-posture-healthy'
                              : c.verdict === 'not_met'
                                ? 'border-posture-critical text-posture-critical'
                                : 'border-ink-200 text-ink-700',
                          )}
                        >
                          {SUBMISSION_VERDICT_LABELS[c.verdict] ?? 'Not shown in the document'}
                        </span>
                        <span className="text-ink-900">{c.criterion}</span>
                      </div>
                      {c.note ? <p className="mt-0.5 text-ink-700">{c.note}</p> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-body-sm text-ink-500">
                  No criteria reading: this deliverable had no acceptance criteria when the document
                  was read.
                </p>
              )}
            </AiSurface>
          </Section>
        ) : null}

        <Section title="Source">
          {e.source_change_order ? (
            // C2-22: the change order that added the item is its source,
            // whether or not a passage was cited alongside it.
            <p className="mb-2 text-body-sm text-ink-700">
              {sourceWord(e)}: added to the register by that change order.
            </p>
          ) : null}
          {cite ? (
            <div className="flex items-center gap-2">
              <CitationChip cite={cite} onOpen={() => setOpenCite(cite)} />
              <span className="text-meta text-ink-500">Open to read the cited passage</span>
            </div>
          ) : e.source_change_order ? null : (
            <p className="text-body-sm text-ink-500">
              {e.origin === 'manually_authored'
                ? 'Added manually. No SOW citation.'
                : 'Approved from an AI proposal with no stored citation.'}
            </p>
          )}
        </Section>

        <Section title="Submittal status">
          <div className="space-y-3 rounded-card border border-ink-100 px-4 py-3">
            <div className="flex items-center gap-2 text-body-sm text-ink-700">
              Now <VerbChip verb={state.verb} />
              {e.status === 'under_review' ? <span>(under review)</span> : null}
            </div>
            {e.status === 'revise_resubmit' && e.revision_reason ? (
              <div className="rounded-card bg-paper-50 px-3 py-2 text-body-sm text-ink-700">
                <div className="text-meta uppercase tracking-wide text-ink-500">
                  Sent back for revision · reason for the vendor
                </div>
                “{e.revision_reason}”
              </div>
            ) : null}
            {nextSteps.length === 0 ? (
              <p className="text-meta text-ink-500">Final. Kept for audit and history.</p>
            ) : confirmTo && REASON_REQUIRED_FOR.has(confirmTo) ? (
              <div className="space-y-2">
                <label className="block text-body-sm text-ink-700">
                  {confirmTo === 'rejected'
                    ? 'Why is it rejected? This is final. Kept with the deliverable and shown in its history.'
                    : 'What should the vendor fix? Kept with the deliverable and shown in its history.'}
                  <textarea
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- the reason is the sole purpose of this step
                    autoFocus
                    value={reason}
                    onChange={(ev) => setReason(ev.target.value)}
                    rows={2}
                    placeholder={
                      confirmTo === 'rejected'
                        ? 'e.g. The test strategy does not cover the integrations the SOW names; a resubmission will not close the gap.'
                        : 'e.g. Section 4 is missing the regression suite the SOW asks for.'
                    }
                    className="mt-1 w-full rounded-card border border-ink-100 bg-paper-0 px-3 py-2 text-body-sm text-ink-900 placeholder:text-ink-300 focus:border-ink-300 focus:outline-none"
                  />
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={transition.isPending || !reasonOk}
                    onClick={() => void move(confirmTo)}
                    className={cn(
                      confirmTo === 'rejected'
                        ? 'bg-posture-critical text-paper-0 hover:bg-posture-critical'
                        : 'bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700',
                    )}
                  >
                    {transition.isPending ? <Spinner /> : null}
                    {confirmTo === 'rejected' ? 'Reject' : 'Send back'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmTo(null)}
                  >
                    Cancel
                  </Button>
                  {!reasonOk ? (
                    <span className="text-meta text-ink-500">
                      One sentence, at least {MIN_REASON_CHARS} characters.
                    </span>
                  ) : null}
                </div>
              </div>
            ) : confirmTo ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-body-sm text-ink-700">
                  {transitionLabel(e.status, confirmTo)}? This is final.
                </span>
                <Button
                  type="button"
                  size="sm"
                  disabled={transition.isPending}
                  onClick={() => void move(confirmTo)}
                  className={cn(
                    confirmTo === 'rejected'
                      ? 'bg-posture-critical text-paper-0 hover:bg-posture-critical'
                      : 'bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700',
                  )}
                >
                  {transition.isPending ? <Spinner /> : null}
                  Confirm
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmTo(null)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {secondary.map((to) => (
                  <Button
                    key={to}
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={transition.isPending}
                    onClick={() => (needsStep(to) ? setConfirmTo(to) : void move(to))}
                  >
                    {transitionLabel(e.status, to)}
                  </Button>
                ))}
              </div>
            )}
            {transition.error ? (
              <ErrorMessage error={labelledError(transition.error, TRANSITION_FIELDS)} />
            ) : null}
          </div>
        </Section>

        <Section
          title="Verification"
          aside={
            <button
              type="button"
              onClick={onVerify}
              className="inline-flex items-center gap-1 text-meta font-medium text-ink-700 hover:underline"
            >
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Update
            </button>
          }
        >
          <div className="flex items-center gap-3 text-body-sm text-ink-700">
            <PostureChip status={verification.posture} dot>
              {verification.label}
            </PostureChip>
            <span className="text-meta text-ink-500">
              Vendor says {e.vendor_claim} · Avero {e.avero_verification.replace('_', ' ')}
            </span>
          </div>
        </Section>

        <Section title="History">
          {history.isLoading ? (
            <div className="flex items-center gap-2 text-meta text-ink-500">
              <Spinner /> Loading history…
            </div>
          ) : (history.data?.items.length ?? 0) === 0 ? (
            <p className="text-body-sm text-ink-500">No status changes yet.</p>
          ) : (
            <ul className="divide-y divide-ink-100">
              {history.data!.items.map((h, i) => (
                <li key={`${h.created_at}-${i}`} className="flex items-baseline gap-3 py-2">
                  <span className="w-24 shrink-0 font-mono text-mono-sm text-ink-500">
                    {formatDay(h.created_at.slice(0, 10))}
                  </span>
                  <span className="text-body-sm text-ink-900">
                    {h.from_status ? `${statusWord(h.from_status)} → ` : ''}
                    {statusWord(h.to_status)}
                    {h.reason ? (
                      <span className="block text-meta text-ink-500">“{h.reason}”</span>
                    ) : null}
                  </span>
                  <span className="ml-auto text-meta text-ink-500">{h.actor_name ?? 'System'}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <div className="flex items-center gap-2 border-t border-ink-100 bg-paper-50 px-6 py-3">
        <Button type="button" variant="outline" size="sm" onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          Edit
        </Button>
        <span className="flex-1" />
        {primary ? (
          <Button
            type="button"
            size="sm"
            disabled={transition.isPending || confirmTo !== null}
            className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
            onClick={() => (needsStep(primary) ? setConfirmTo(primary) : void move(primary))}
          >
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            {transitionLabel(e.status, primary)}
          </Button>
        ) : null}
      </div>

      {openCite ? (
        <CitationSourceModal
          engagementId={engagementId}
          cite={openCite}
          open
          onOpenChange={(o) => !o && setOpenCite(null)}
        />
      ) : null}
    </>
  );
}

// ===========================================================================
// Edit / create
// ===========================================================================

type PaymentChoice = 'yes' | 'no' | 'unknown';

const inputCls =
  'h-9 w-full rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm text-ink-900 placeholder:text-ink-300 focus:border-ink-300 focus:outline-none focus:ring-2 focus:ring-accent-amber-600';

const MIN_RATIONALE_CHARS = 10;

function EditPane({
  engagementId,
  entry: e,
  onDone,
  onOverrideCaptured,
}: {
  engagementId: string;
  entry: EdlEntry | null;
  onDone: () => void;
  onOverrideCaptured: (overrideId: string, reasonTag: string) => void;
}) {
  const creating = e === null;
  const needsOverride = e !== null && e.origin !== 'manually_authored';
  // The IV&V-gate flag is a milestone's, and only a lead sets it (T-0217).
  const isLead = useCanFlagIvvGate();
  const canFlagGate = e?.item_type === 'milestone' && isLead;
  const create = useCreateDeliverableMutation(engagementId);
  const update = useUpdateDeliverableMutation(engagementId);
  const taxonomy = useOverrideTaxonomyQuery();

  const [title, setTitle] = useState(e?.title ?? '');
  const [sowRef, setSowRef] = useState(e?.sow_ref ?? '');
  const [description, setDescription] = useState(e?.description ?? '');
  const [category, setCategory] = useState(e?.category ?? '');
  const [due, setDue] = useState(e?.expected_delivery_date ?? '');
  const [payment, setPayment] = useState<PaymentChoice>(
    e?.is_payment_tied === true ? 'yes' : e?.is_payment_tied === false ? 'no' : 'unknown',
  );
  const [ivvGate, setIvvGate] = useState(e?.is_ivv_gate === true);
  const [criteria, setCriteria] = useState<string[]>(
    e && e.acceptance_criteria.length > 0 ? [...e.acceptance_criteria] : [''],
  );
  const [reasonTag, setReasonTag] = useState('');
  const [rationale, setRationale] = useState('');

  const overrideValid =
    !needsOverride || (reasonTag.length > 0 && rationale.trim().length >= MIN_RATIONALE_CHARS);
  const canSave = title.trim().length > 0 && overrideValid;
  const saving = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  async function save() {
    if (!canSave) return;
    const details: DeliverableDetails = {
      title: title.trim(),
      sow_ref: sowRef.trim() || null,
      description: description.trim() || null,
      category: category.trim() || null,
      expected_delivery_date: due || null,
      is_payment_tied: payment === 'yes' ? true : payment === 'no' ? false : null,
      ...(canFlagGate ? { is_ivv_gate: ivvGate } : {}),
      acceptance_criteria: criteria.map((c) => c.trim()).filter((c) => c.length > 0),
    };
    try {
      if (creating) {
        await create.mutateAsync({ ...details, title: title.trim() });
        onDone();
        return;
      }
      const out = await update.mutateAsync({
        registerEntryId: e.register_entry_id,
        details,
        ...(needsOverride
          ? { override: { reason_tag: reasonTag, rationale: rationale.trim() } }
          : {}),
      });
      if (out.override_record_id) onOverrideCaptured(out.override_record_id, reasonTag);
      else onDone();
    } catch {
      // Surfaced through the mutation's error state below the form.
    }
  }

  return (
    <>
      <DrawerHeader
        kicker={
          creating ? 'New deliverable' : `${e.sow_ref ?? 'No SOW ref'} · ${itemWord(e)} · Edit`
        }
        title={creating ? 'Add a deliverable' : e.title}
      />

      <form
        className="flex-1 space-y-5 overflow-y-auto px-6 py-5"
        onSubmit={(ev) => {
          ev.preventDefault();
          void save();
        }}
      >
        <div className="grid grid-cols-[1fr_140px] gap-3">
          <label className="block">
            <span className="mb-1.5 block text-meta font-medium text-ink-700">Name</span>
            <input
              className={inputCls}
              value={title}
              onChange={(ev) => setTitle(ev.target.value)}
              placeholder="e.g. Data Conversion Plan"
              required
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-meta font-medium text-ink-700">SOW ref</span>
            <input
              className={cn(inputCls, 'font-mono')}
              value={sowRef}
              onChange={(ev) => setSowRef(ev.target.value)}
              placeholder="D-14"
            />
          </label>
        </div>

        <label className="block">
          <span className="mb-1.5 block text-meta font-medium text-ink-700">
            What the vendor hands over{' '}
            <span className="font-normal text-ink-500">· one sentence</span>
          </span>
          <input
            className={inputCls}
            value={description}
            onChange={(ev) => setDescription(ev.target.value)}
            placeholder="e.g. Mapping and reconciliation plan for legacy HR and payroll data"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1.5 block text-meta font-medium text-ink-700">Due date</span>
            <input
              type="date"
              className={inputCls}
              value={due}
              onChange={(ev) => setDue(ev.target.value)}
            />
            {e?.due_text ? (
              <span className="mt-1 block text-meta text-ink-500">SOW timing: {e.due_text}</span>
            ) : null}
          </label>
          <label className="block">
            <span className="mb-1.5 block text-meta font-medium text-ink-700">Category</span>
            <input
              className={inputCls}
              value={category}
              onChange={(ev) => setCategory(ev.target.value)}
              placeholder="e.g. Data, Testing, Cutover"
            />
          </label>
        </div>

        <fieldset>
          <legend className="mb-1.5 text-meta font-medium text-ink-700">
            Is a payment tied to accepting it?
          </legend>
          <div
            role="radiogroup"
            aria-label="Payment tie"
            className="inline-flex overflow-hidden rounded-card border border-ink-200"
          >
            {(
              [
                ['yes', 'Yes'],
                ['no', 'No'],
                ['unknown', 'Not stated'],
              ] as const
            ).map(([value, text]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={payment === value}
                onClick={() => setPayment(value)}
                className={cn(
                  'border-r border-ink-200 px-3 py-1.5 text-meta font-medium last:border-r-0',
                  payment === value
                    ? 'bg-ink-900 text-paper-0'
                    : 'bg-paper-0 text-ink-700 hover:bg-paper-50',
                )}
              >
                {text}
              </button>
            ))}
          </div>
        </fieldset>

        {canFlagGate ? (
          <label className="flex items-center gap-2 text-body-sm text-ink-900">
            <input
              type="checkbox"
              checked={ivvGate}
              onChange={(ev) => setIvvGate(ev.target.checked)}
            />
            IV&V gate in the vendor contract
          </label>
        ) : null}

        <fieldset>
          <legend className="mb-1.5 text-meta font-medium text-ink-700">Acceptance criteria</legend>
          <div className="space-y-2">
            {criteria.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  aria-label={`Acceptance criterion ${i + 1}`}
                  className={inputCls}
                  value={c}
                  placeholder="e.g. Reconciliation variance under 0.5%"
                  onChange={(ev) =>
                    setCriteria((prev) => prev.map((x, xi) => (xi === i ? ev.target.value : x)))
                  }
                />
                <button
                  type="button"
                  aria-label={`Remove acceptance criterion ${i + 1}`}
                  disabled={criteria.length === 1}
                  onClick={() => setCriteria((prev) => prev.filter((_, xi) => xi !== i))}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-card border border-ink-200 text-ink-500 hover:bg-paper-100 disabled:opacity-40"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setCriteria((prev) => [...prev, ''])}
              className="inline-flex items-center gap-1 text-meta font-medium text-ink-700 hover:underline"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add criterion
            </button>
          </div>
        </fieldset>

        {needsOverride ? (
          <fieldset className="space-y-3 rounded-card border border-ai-surface-border bg-ai-surface px-4 py-3">
            <legend className="px-1 text-meta font-semibold text-ai-ink">
              Why are you changing what the AI extracted?
            </legend>
            <select
              aria-label="Reason"
              className={inputCls}
              value={reasonTag}
              onChange={(ev) => setReasonTag(ev.target.value)}
            >
              <option value="">Choose a reason…</option>
              {(taxonomy.data?.tags ?? []).map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
            <textarea
              aria-label="Rationale"
              className="min-h-[72px] w-full rounded-card border border-ink-200 bg-paper-0 px-3 py-2 text-body-sm text-ink-900 placeholder:text-ink-300 focus:border-ink-300 focus:outline-none focus:ring-2 focus:ring-accent-amber-600"
              value={rationale}
              onChange={(ev) => setRationale(ev.target.value)}
              placeholder="One sentence the audit trail will keep, e.g. PMO confirmed the due date for M+4 is 1 Nov 2026."
            />
            <p className="text-meta text-ai-ink">
              Recorded as a consultant override. Related AI proposals are re-checked afterwards.
            </p>
          </fieldset>
        ) : null}

        {error ? <ErrorMessage error={error} /> : null}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>

      <div className="flex items-center gap-2 border-t border-ink-100 bg-paper-50 px-6 py-3">
        <span className="flex-1" />
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!canSave || saving}
          onClick={() => void save()}
          className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
        >
          {saving ? <Spinner /> : <Check className="h-3.5 w-3.5" aria-hidden="true" />}
          {creating ? 'Add deliverable' : needsOverride ? 'Save override' : 'Save'}
        </Button>
      </div>
    </>
  );
}
